import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Modal,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { financialPOAService } from '../../../../services/financialPOAInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('FinancialPOAInput');

type FinancialPOAInputScreenRouteProp = RouteProp<RootStackParamList, 'FinancialPOAInput'>;

const POWER_OPTIONS = [
  'Banking transactions',
  'Real estate transactions',
  'Investment management',
  'Tax matters',
  'Insurance claims',
  'Business operations',
  'Legal matters',
  'Healthcare payments',
];

export const FinancialPOAInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<FinancialPOAInputScreenRouteProp>();

  const { financialPOAData, isEditing } = route.params || {};

  // Helper function to format date input as MM/DD/YYYY
  const formatDate = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 4) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    } else if (cleaned.length <= 8) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4) + '/' + cleaned.slice(4, 8);
    }
    return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4) + '/' + cleaned.slice(4, 8);
  };

  // Determine if initial effectiveDate is "Upon Incapacity" or a specific date
  const initialUponIncapacity = !financialPOAData?.effectiveDate ||
    financialPOAData?.effectiveDate === 'Upon Incapacity';

  const [agentName, setAgentName] = useState(financialPOAData?.agentName || '');
  const [agentContact, setAgentContact] = useState(financialPOAData?.agentContact || '');
  const [agentNotified, setAgentNotified] = useState(financialPOAData?.agentNotified || '');
  const [alternateAgentName, setAlternateAgentName] = useState(financialPOAData?.alternateAgentName || '');
  const [alternateAgentContact, setAlternateAgentContact] = useState(financialPOAData?.alternateAgentContact || '');
  const [powersGranted, setPowersGranted] = useState<string[]>(
    typeof financialPOAData?.powersGranted === 'string'
      ? JSON.parse(financialPOAData.powersGranted || '[]')
      : financialPOAData?.powersGranted || []
  );
  const [uponIncapacity, setUponIncapacity] = useState(initialUponIncapacity);
  const [effectiveDate, setEffectiveDate] = useState(
    initialUponIncapacity ? '' : (financialPOAData?.effectiveDate || '')
  );
  const [physicalLocation, setPhysicalLocation] = useState(financialPOAData?.physicalLocation || '');
  const [specificInstructions, setSpecificInstructions] = useState(financialPOAData?.specificInstructions || '');
  const [saving, setSaving] = useState(false);
  const [showNotifiedModal, setShowNotifiedModal] = useState(false);

  const isValidEmail = (email: string) => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const togglePower = (power: string) => {
    setPowersGranted(prev =>
      prev.includes(power) ? prev.filter(p => p !== power) : [...prev, power]
    );
  };

  const handleNotifiedSelect = (value: string) => {
    setAgentNotified(value);
    setShowNotifiedModal(false);
  };

  const handleSave = async () => {
    if (!agentName.trim()) {
      Alert.alert('Error', 'Please enter the agent\'s name');
      return;
    }

    if (agentContact && !isValidEmail(agentContact)) {
      Alert.alert('Error', 'Please enter a valid email address for the agent');
      return;
    }

    if (alternateAgentContact && !isValidEmail(alternateAgentContact)) {
      Alert.alert('Error', 'Please enter a valid email address for the backup agent');
      return;
    }

    try {
      setSaving(true);

      const data = {
        hasFinancialPOA: true,
        agentName: agentName.trim(),
        agentContact: agentContact.trim(),
        agentNotified: agentNotified,
        alternateAgentName: alternateAgentName.trim(),
        alternateAgentContact: alternateAgentContact.trim(),
        powersGranted: JSON.stringify(powersGranted),
        effectiveDate: uponIncapacity ? 'Upon Incapacity' : effectiveDate.trim(),
        physicalLocation: physicalLocation.trim(),
        specificInstructions: specificInstructions.trim(),
      };

      let savedData;
      if (isEditing && financialPOAData?.id) {
        savedData = await financialPOAService.updateFinancialPOA(financialPOAData.id, data);
        const updatedData = savedData?.data || savedData;
        logger.debug('Financial POA updated successfully with ID:', updatedData.id);
        navigation.navigate('FinancialPOALockUnlock', { financialPOAId: updatedData.id });
      } else {
        savedData = await financialPOAService.createFinancialPOA(data);
        const createdData = savedData?.data || savedData;
        logger.debug('Financial POA saved successfully with ID:', createdData.id);
        navigation.navigate('FinancialPOALockUnlock', { financialPOAId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving financial POA:', error);
      Alert.alert('Error', 'Failed to save financial POA information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Image source={require('../../../../assets/icons/back.png')} style={[styles.backIcon, { tintColor: '#fff' }]} />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image source={require('../../../../assets/icons/keyimage.png')} style={styles.backgroundKey} />
        <Image source={require('../../../../assets/logo.png')} style={styles.logo} resizeMode="contain" />
        <Text style={styles.title}>Add Financial POA Details</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <Text style={styles.sectionTitle}>Primary Agent:</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={agentName}
              onChangeText={setAgentName}
              placeholder="Enter agent's full name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email (Optional):</Text>
            <TextInput
              style={styles.input}
              value={agentContact}
              onChangeText={setAgentContact}
              placeholder="Enter email address"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Have they been notified?</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => setShowNotifiedModal(true)}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !agentNotified && { color: '#A9A9A9' }
              ]}>
                {agentNotified || 'Select Yes/No'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          <Text style={styles.sectionTitle}>Backup Agent (Optional):</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={alternateAgentName}
              onChangeText={setAlternateAgentName}
              placeholder="Enter backup agent's name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email (Optional):</Text>
            <TextInput
              style={styles.input}
              value={alternateAgentContact}
              onChangeText={setAlternateAgentContact}
              placeholder="Enter email address"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <Text style={styles.sectionTitle}>Powers Granted:</Text>
          <View style={styles.powersContainer}>
            {POWER_OPTIONS.map((power) => (
              <TouchableOpacity
                key={power}
                style={[styles.powerItem, powersGranted.includes(power) && styles.powerItemSelected]}
                onPress={() => togglePower(power)}
              >
                <View style={[styles.checkbox, powersGranted.includes(power) && styles.checkboxSelected]}>
                  {powersGranted.includes(power) && (
                    <Image source={require('../../../../assets/icons/check.png')} style={styles.checkmark} />
                  )}
                </View>
                <Text style={styles.powerText}>{power}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={styles.sectionTitle}>Document Details:</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Effective Date:</Text>
            <View style={styles.toggleContainer}>
              <Text style={styles.toggleLabel}>Upon Incapacity</Text>
              <TouchableOpacity
                style={[styles.toggleButton, uponIncapacity && styles.toggleButtonActive]}
                onPress={() => setUponIncapacity(!uponIncapacity)}
              >
                <View style={[styles.toggleCircle, uponIncapacity && styles.toggleCircleActive]} />
              </TouchableOpacity>
            </View>
            {!uponIncapacity && (
              <TextInput
                style={[styles.input, { marginTop: 10 }]}
                value={effectiveDate}
                onChangeText={(text) => setEffectiveDate(formatDate(text))}
                placeholder="MM/DD/YYYY"
                placeholderTextColor="#A9A9A9"
                keyboardType="numeric"
                maxLength={10}
              />
            )}
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Document Location:</Text>
            <TextInput
              style={styles.input}
              value={physicalLocation}
              onChangeText={setPhysicalLocation}
              placeholder="Where is the document stored?"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Instructions (Optional):</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={specificInstructions}
              onChangeText={setSpecificInstructions}
              placeholder="Any additional notes or instructions"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      {/* Yes/No Modal for Notified */}
      <Modal
        visible={showNotifiedModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowNotifiedModal(false)}
        statusBarTranslucent
        hardwareAccelerated={true}
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={{ flex: 1 }}
            activeOpacity={1}
            onPress={() => setShowNotifiedModal(false)}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Have they been notified?</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowNotifiedModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.optionList}>
              <TouchableOpacity
                style={styles.optionItem}
                onPress={() => handleNotifiedSelect('Yes')}
              >
                <Text style={styles.optionItemText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.optionItem}
                onPress={() => handleNotifiedSelect('No')}
              >
                <Text style={styles.optionItemText}>No</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#29BFC0' },
  header: { flexDirection: 'row', alignItems: 'center', padding: 15, paddingTop: 25, position: 'relative' },
  backButton: { position: 'absolute', left: 50, padding: 15, paddingTop: 50, zIndex: 1 },
  backIcon: { width: 32, height: 32, tintColor: '#fff' },
  topSection: { alignItems: 'center', paddingVertical: 20 },
  backgroundKey: { position: 'absolute', width: '85%', height: '85%', opacity: 0.1 },
  logo: { width: 125, height: 125, marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 'bold', color: '#FFFFFF', marginBottom: 8, paddingHorizontal: 30, textAlign: 'center' },
  inputContainer: { flex: 1, backgroundColor: '#F3F6F8', borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingTop: 20 },
  scrollView: { paddingHorizontal: 16, paddingBottom: 120 },
  sectionTitle: { fontSize: 20, fontWeight: 'bold', color: '#1C2C4F', marginBottom: 16, marginTop: 10 },
  inputGroup: { marginBottom: 16 },
  label: { fontSize: 16, fontWeight: '600', color: '#6B7280', marginBottom: 8 },
  input: { backgroundColor: '#FFFFFF', borderRadius: 25, padding: 15, fontSize: 16, color: '#1C2C4F', borderWidth: 1, borderColor: '#d8dbdd' },
  textArea: { minHeight: 80, textAlignVertical: 'top' },
  powersContainer: { marginBottom: 16 },
  powerItem: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFFFFF', borderRadius: 12, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: '#E5E5E5' },
  powerItemSelected: { borderColor: '#29BFC0', borderWidth: 2 },
  checkbox: { width: 24, height: 24, borderRadius: 12, borderWidth: 2, borderColor: '#E5E5E5', alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  checkboxSelected: { backgroundColor: '#29BFC0', borderColor: '#29BFC0' },
  checkmark: { width: 14, height: 14, tintColor: '#FFFFFF' },
  powerText: { fontSize: 16, color: '#1C2C4F' },
  submitButtonContainer: { alignItems: 'center', marginTop: 20, marginBottom: 40 },
  saveButton: { backgroundColor: '#29BFC0', borderRadius: 25, paddingVertical: 15, paddingHorizontal: 60, alignItems: 'center', marginBottom: 120, minWidth: 200 },
  saveButtonText: { color: '#FFFFFF', fontSize: 16, fontWeight: '600' },
  bottomPadding: { height: 120 },
  dropdownIcon: { width: 20, height: 20 },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    padding: ms(15),
    borderRadius: ms(25),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  toggleButton: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#d8dbdd',
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleButtonActive: {
    backgroundColor: '#29BFC0',
  },
  toggleCircle: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
  },
  toggleCircleActive: {
    transform: [{ translateX: 20 }],
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  optionList: {
    padding: ms(16),
  },
  optionItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  optionItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});
